import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import logoAsset from "@/assets/logo.png.asset.json";
import { Button } from "@/components/ui/button";

type NavLink = { label: string; href: string; external?: boolean };

const NAV_GROUPS = [
  { label: "Our Work", links: [
    { label: "Trust Operating System", href: "/trust-operating-system" },
    { label: "CEO Practitioner Network", href: "https://ceopractitionernetwork.com", external: true },
    { label: "Podcast", href: "/podcast" },
    { label: "Conference", href: "/conference" },
  ] },
  { label: "Work With Us", links: [
    { label: "For Agencies", href: "/for-agencies" },
    { label: "For Organizations", href: "/for-organizations" },
    { label: "Speaking", href: "/book-carlai" },
  ] },
] satisfies Array<{ label: string; links: NavLink[] }>;
const NAV_CLASS = "min-h-11 text-[12.5px] font-medium uppercase tracking-[0.08em]";

const BOOK_HREF = "/book-carlai" as const;

function NavItem({
  link,
  onClick,
  active,
  className,
}: {
  link: NavLink;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}) {
  const base =
    "relative whitespace-nowrap text-[12.5px] font-medium tracking-[0.08em] uppercase transition-colors " +
    (active ? "text-warm-white" : "text-warm-white/70 hover:text-warm-white");
   const cls = "relative " + (className ?? base);
  const content = (
    <>
      {link.label}
      {active && (
        <span
          aria-hidden
          className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-px w-6 bg-gold"
        />
      )}
    </>
  );
  return (
    <a
      href={link.href}
      target={link.external ? "_blank" : undefined}
      rel={link.external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cls}
    >
      {content}
    </a>
  );
}

export function NavBar() {
  const [open, setOpen] = useState(false);
  const [desktopGroup, setDesktopGroup] = useState("");
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    setOpen(false);
    setDesktopGroup("");
  }, [pathname]);

  // Prevent body scroll while mobile menu is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close the mobile menu on Escape and return focus to the toggle
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        document.getElementById("mobile-nav-toggle")?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);


  return (
    <header className="sticky top-0 z-50 bg-navy/90 backdrop-blur-md border-b border-warm-white/10">
      <nav
        aria-label="Primary"
        className="mx-auto max-w-7xl px-5 sm:px-8 h-16 lg:h-20 grid grid-cols-[auto_1fr_auto] items-center gap-6"
      >
        <Link to="/" className="flex items-center leading-none shrink-0" aria-label="Tables and Bridges - home">
          <img
            src={logoAsset.url}
            alt=""
            width={48}
            height={48}
            className="h-9 sm:h-11 w-auto"
          />
          <span className="sr-only">Tables and Bridges</span>
        </Link>

        <div className="hidden lg:flex justify-center">
          <NavigationMenu aria-label="Desktop navigation" value={desktopGroup} onValueChange={setDesktopGroup}>
            <NavigationMenuList className="gap-5">
              <NavigationMenuItem><NavigationMenuLink asChild><Link to="/about" className={NAV_CLASS + " inline-flex items-center " + (pathname === "/about" ? "text-gold" : "text-warm-white/70 hover:text-warm-white")} aria-current={pathname === "/about" ? "page" : undefined}>About</Link></NavigationMenuLink></NavigationMenuItem>
              {NAV_GROUPS.map(group => {
                const active = group.links.some(link => !link.external && pathname === link.href);
                return <NavigationMenuItem key={group.label} value={group.label}>
                  <NavigationMenuTrigger className={NAV_CLASS + " rounded-sm !bg-transparent hover:!bg-warm-white/5 focus:!bg-warm-white/5 focus-visible:outline-2 focus-visible:outline-gold data-[state=open]:!bg-warm-white/5 data-[state=open]:!text-gold " + (active ? "!text-gold" : "!text-warm-white/70 hover:!text-warm-white")}>{group.label}</NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-navy text-warm-white">
                    <ul className="w-80 p-3 space-y-1">{group.links.map(link => <li key={link.label}><NavigationMenuLink asChild><a href={link.href} target={link.external ? "_blank" : undefined} rel={link.external ? "noopener noreferrer" : undefined} aria-current={!link.external && pathname === link.href ? "page" : undefined} className={"flex min-h-11 items-center px-4 py-3 text-sm hover:bg-warm-white/5 " + (!link.external && pathname === link.href ? "text-gold" : "text-warm-white/85")}>{link.label}{link.external && <span aria-hidden className="ml-2 text-gold">↗</span>}</a></NavigationMenuLink></li>)}</ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>;
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        <span className="lg:hidden" aria-hidden />

        <div className="flex items-center justify-end gap-3 shrink-0">
          <Link to={BOOK_HREF} className="hidden md:inline-flex btn-base btn-primary !py-2.5 !px-5 text-[11.5px]">
            Book Carlai
          </Link>
          <Button
            type="button"
            id="mobile-nav-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav-panel"
            onClick={() => setOpen((v) => !v)}
            variant="ghost"
            size="icon"
            className="lg:hidden inline-flex items-center justify-center min-h-11 min-w-11 -mr-2 text-warm-white"
          >
            <svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              {open ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="13" x2="20" y2="13" />
                  <line x1="4" y1="19" x2="14" y2="19" />
                </>
              )}
            </svg>
          </Button>
        </div>
      </nav>

      {open && (
        <div
          id="mobile-nav-panel"
          className="lg:hidden border-t border-warm-white/10 bg-navy px-6 py-8 max-h-[calc(100dvh-4rem)] overflow-y-auto"
        >
          <ul className="flex flex-col gap-1 max-w-md mx-auto">
            <li className="border-b border-warm-white/10"><NavItem link={{ label: "About", href: "/about" }} onClick={() => setOpen(false)} active={pathname === "/about"} className={NAV_CLASS + " flex items-center " + (pathname === "/about" ? "text-gold" : "text-warm-white/85")} /></li>
            <li><Accordion type="multiple">
              {NAV_GROUPS.map(group => <AccordionItem key={group.label} value={group.label} className="border-warm-white/10">
                <AccordionTrigger className={NAV_CLASS + " font-sans hover:no-underline " + (group.links.some(link => !link.external && pathname === link.href) ? "text-gold" : "text-warm-white/85")}>{group.label}</AccordionTrigger>
                <AccordionContent><ul className="pl-4 space-y-1">{group.links.map(link => <li key={link.label}><NavItem link={link} active={!link.external && pathname === link.href} onClick={() => setOpen(false)} className={"flex min-h-11 items-center py-2 text-sm " + (!link.external && pathname === link.href ? "text-gold" : "text-warm-white/75 hover:text-warm-white")} /></li>)}</ul></AccordionContent>
              </AccordionItem>)}
            </Accordion></li>
            <li className="pt-6">
              <Link
                to={BOOK_HREF}
                onClick={() => setOpen(false)}
                className="btn-base btn-primary w-full"
              >
                Book Carlai
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
